import { Component, OnDestroy, OnInit } from '@angular/core';
import { Registration } from 'app/model/registration';
import { GeolocationService } from 'app/shared/services/data/geolocation.service';
import { RegistrationTrackerNavbarService } from 'app/shared/services/data/registration/registration-tracker-navbar.service';
import { ToastAlertService } from 'app/shared/services/data/toast-alert.service';
import { LoginService } from 'app/shared/services/login/login.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { interval, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-registration-tracker-navbar',
  templateUrl: './registration-tracker-navbar.component.html',
  styleUrls: ['./registration-tracker-navbar.component.scss']
})
export class RegistrationTrackerNavbarComponent implements OnInit, OnDestroy {
  private readonly MAX_ATTEMPS_TO_GET_LOCATION: number = 5

  private seconds: number = 0

  private interval: Observable<number>
  private intervalSubscription: Subscription

  private registrationId: number

  public timer: string

  public hasStarted: boolean
  public timerIsRunning: boolean
  public hasFinished: boolean

  /**
  * Constructor
  *
  * @param {RegistrationTrackerNavbarService} registrationTrackerNavbarService
  * @param {GeolocationService} geolocationService
  * @param {ToastAlertService} toastAlertService
  * @param {LoginService} loginService
  * @param {NgxSpinnerService} spinner
  * 
  */
  constructor(
    private registrationTrackerNavbarService: RegistrationTrackerNavbarService,
    private geolocationService: GeolocationService,
    private toastAlertService: ToastAlertService,
    private loginService: LoginService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.initializeValues()
  }

  ngOnDestroy(): void {
    this.intervalSubscription?.unsubscribe()
  }

  private initializeValues(): void {
    this.interval = interval(1000)

    this.timer = '00:00:00'

    this.hasStarted = false
    this.timerIsRunning = false
    this.hasFinished = false

    this.checkIfEmployeeHasCheckedToday()
  }

  private startInterval(initialSeconds: number): void {
    this.setStartTime(initialSeconds)

    this.calculateTimer()

    setTimeout(() => {
      this.intervalSubscription = this.interval.subscribe(() => this.calculateTimer())
    })
  }

  private setStartTime(initialSeconds: number): void {
    this.registrationTrackerNavbarService.setStartTime(initialSeconds)
  }

  private calculateTimer(): void {
    const time = this.registrationTrackerNavbarService.calculateTimer()

    this.timer = `${time.hours}:${time.minutes}:${time.seconds}`
  }

  private checkIfEmployeeHasCheckedToday(): void {
    this.registrationTrackerNavbarService.checkIfEmployeeHasCheckedToday()
      .subscribe((data) => {
        if (!data) return

        if (this.registrationTrackerNavbarService.checkIfItHasLatest(data)) {
          this.hasFinished = true

          return
        }

        const currentRegistration: Registration | undefined = this.registrationTrackerNavbarService.checkIfEmployeeHasAnyCheckedUnfinished(data)

        if (currentRegistration) data.splice(data.indexOf(currentRegistration), 1)

        const secondsHasPassed = this.registrationTrackerNavbarService.getSecondsHasPassedFromRegistrations(data)

        this.hasStarted = true

        if (currentRegistration) {
          this.timerIsRunning = true

          this.registrationId = currentRegistration.id

          const secondsHasPassedFromCurrent = this.registrationTrackerNavbarService
            .getSecondsHasPassedFromDate(currentRegistration.start_date, currentRegistration.start_hour)

          const totalSecondsHasPassed = secondsHasPassed + secondsHasPassedFromCurrent

          this.startInterval(totalSecondsHasPassed)
        } else {
          this.setStartTime(secondsHasPassed)

          this.calculateTimer()
        }
      })
  }

  private catchLocationError(): Observable<null> {
    const alertTitle = 'Sin localización'
    const alertMessage = '¡Hubo un problema al obtener la localización!'
    const alertType = 'warning'

    this.sendAlert(alertTitle, alertMessage, alertType)

    this.spinner.hide()

    return of(null)
  }

  private sendAlert(title: string, message: string, type: string = 'success'): void {
    this.toastAlertService.auto(type, message, title)
  }

  private setStopTimerValues(): void {
    this.timer = '00:00:00'
    this.timerIsRunning = false
    this.hasFinished = true
    this.hasStarted = false
  }

  public start(): void {
    this.spinner.show()

    this.geolocationService.getAccuratePosition(this.MAX_ATTEMPS_TO_GET_LOCATION)
      .pipe(catchError(() => this.catchLocationError()))
      .subscribe((position) => {
        this.registrationTrackerNavbarService.startTimer(position)
          .pipe(
            finalize(() => this.spinner.hide())
          )
          .subscribe({
            next: () => this.checkIfEmployeeHasCheckedToday(),
            error: () => {
              let alertTitle = 'Ha ocurrido un error'
              let alertMessage = '¡Ha ocurrido un error al intentar fichar!'
              let alertType = 'error'

              this.sendAlert(alertTitle, alertMessage, alertType)
            }
          })
      })
  }

  public pause(): void {
    this.spinner.show()

    let alertTitle = 'Pausado Correctamente'
    let alertMessage = '¡Se ha pausado el fichaje correctamente!'
    let alertType = 'success'

    this.geolocationService.getAccuratePosition(this.MAX_ATTEMPS_TO_GET_LOCATION)
      .pipe(catchError(() => this.catchLocationError()))
      .subscribe((position) => {
        this.registrationTrackerNavbarService.stopTimer(this.registrationId, position, false)
          .pipe(
            finalize(() => {
              this.spinner.hide()

              this.sendAlert(alertTitle, alertMessage, alertType)
            })
          )
          .subscribe({
            next: () => {
              this.timerIsRunning = false

              this.intervalSubscription.unsubscribe()

              this.registrationTrackerNavbarService.resetTime()
            },
            error: () => {
              alertTitle = 'Ha ocurrido un error'
              alertMessage = '¡Ha ocurrido un error al intentar parar el fichaje!'
              alertType = 'error'
            }
          })
      })
  }

  public finish(): void {
    this.spinner.show()

    let alertTitle = 'Fichado Correctamente'
    let alertMessage = '¡Se ha completado el fichaje de hoy correctamente!'
    let alertType = 'success'

    const currentRegistrationId = this.registrationId

    if (currentRegistrationId) {
      this.geolocationService.getAccuratePosition(this.MAX_ATTEMPS_TO_GET_LOCATION)
        .pipe(
          catchError(() => this.catchLocationError())
        )
        .subscribe((position) => {
          this.registrationTrackerNavbarService.stopTimer(currentRegistrationId, position, true)
            .pipe(
              finalize(() => {
                this.spinner.hide()

                this.sendAlert(alertTitle, alertMessage, alertType)
              })
            )
            .subscribe({
              next: () => {
                this.setStopTimerValues()

                this.intervalSubscription.unsubscribe()

                this.registrationTrackerNavbarService.resetTime()

                this.hasFinished = true
              },
              error: () => {
                alertTitle = 'Ha ocurrido un error'
                alertMessage = '¡Ha ocurrido un error al intentar terminar el fichaje!'
                alertType = 'error'
              }
            })
        })
    } else {
      const employeeId = this.loginService.getUser().employee.id

      this.registrationTrackerNavbarService.finallyLastRegistration(employeeId)
        .pipe(
          finalize(() => {
            this.spinner.hide()

            this.sendAlert(alertTitle, alertMessage, alertType)
          })
        )
        .subscribe({
          next: () => this.setStopTimerValues(),
          error: () => {
            alertTitle = 'Ha ocurrido un error'
            alertMessage = '¡Ha ocurrido un error al intentar terminar el fichaje!'
            alertType = 'error'
          }
        })
    }
  }
}
